<script lang="ts">
  import { lockClosedIcon, lockOpenIcon } from '@nrk/core-icons';
  import type { SetGameInfo } from '@pin/schemas/admin';
  import { GRACE_PERIOD_MS } from '@pin/schemas/firestore';
  import { startWith } from 'rxjs';
  import { gameInfo$, setGameInfo } from '../api/gameInfo';
  import { refreshParticipantsStats } from '../api/participant';
  import { addToast } from '../stores/toastStore';
  import {
    formatDuration,
    formatTimeLeft,
    useClock,
  } from '../utils/useClock.svelte';
  import Icon from './Icon.svelte';

  const PRESETS = [
    { label: 'Nå', seconds: 0 },
    { label: '30s', seconds: 30 },
    { label: '1min', seconds: 60 },
    { label: '2min', seconds: 120 },
    { label: '3min', seconds: 180 },
    { label: '5min', seconds: 300 },
  ];

  const gameInfo = gameInfo$.pipe(startWith(null));
  const clock = useClock();

  let isExpanded = $state(false);
  let showConfirm = $state(false);
  let selectedPreset = $state('180');
  let resetFavorites = $state(true);
  let pendingAction: SetGameInfo['action'] | null = $state(null);
  let isSubmitting = $state(false);

  const isLocked = $derived.by(() => {
    const lockedAt = $gameInfo?.state.lockedAt;
    if (lockedAt == null) return false;
    return clock.now >= lockedAt;
  });

  const countdown = $derived(
    isLocked ? null : formatTimeLeft($gameInfo?.state.lockedAt, clock.now)
  );

  const graceCountDown = $derived.by(() => {
    const lockedAt = $gameInfo?.state.lockedAt;
    const notInGracePeriod = lockedAt == null || clock.now < lockedAt;
    if (notInGracePeriod) {
      return null;
    }
    const graceEndsAt = lockedAt + GRACE_PERIOD_MS;
    if (clock.now >= graceEndsAt) {
      return null;
    }
    return formatTimeLeft(graceEndsAt, clock.now);
  });

  const lockSeconds = $derived(Number(selectedPreset) || 0);

  const canLock = $derived($gameInfo != null && !isLocked);
  const hasCountdown = $derived(countdown != null);

  const confirmTitle = $derived.by(() => {
    if (pendingAction === 'lock') {
      return 'Lås runden?';
    }
    if (pendingAction === 'unlock') {
      return 'Lås opp runden?';
    }
    if (pendingAction === 'open') {
      return 'Start ny runde?';
    }
    return '';
  });

  const confirmBody = $derived.by(() => {
    if (pendingAction === 'lock') {
      if (lockSeconds === 0) return 'Stenger umiddelbart.';
      return `Stenger om ${formatDuration(lockSeconds)}.`;
    }
    if (pendingAction === 'unlock')
      return 'Runden åpnes på nytt. Favoritter beholdes.';
    if (pendingAction === 'open')
      return 'En ny runde startes. Favoritter tilbakestilles.';
    return '';
  });

  const toggleExpanded = () => {
    isExpanded = !isExpanded;
    if (!isExpanded) {
      showConfirm = false;
    }
  };

  const handleAction = (action: SetGameInfo['action']) => {
    pendingAction = action;
    showConfirm = true;
  };

  const handleCancel = () => {
    showConfirm = false;
    isExpanded = false;
  };

  const run = async (
    action: () => Promise<void>,
    success: string,
    failure: string,
    onSuccess?: () => void
  ) => {
    isSubmitting = true;
    try {
      await action();
      addToast({ type: 'success', message: success });
      showConfirm = false;
      isExpanded = false;
      onSuccess?.();
    } catch (e) {
      console.error(e);
      addToast({ type: 'error', message: `${failure}` });
    } finally {
      isSubmitting = false;
    }
  };

  const handleConfirm = () => {
    if (pendingAction === 'lock') {
      run(
        () => setGameInfo({ action: 'lock', locksIn: lockSeconds * 1_000 }),
        lockSeconds === 0 ? 'Spillet er låst' : 'Spillet låses',
        'Kunne ikke låse spillet'
      );
    } else if (pendingAction === 'unlock') {
      run(
        () => setGameInfo({ action: 'unlock' }),
        'Runden er åpnet',
        'Kunne ikke låse opp runden'
      );
    } else if (pendingAction === 'open') {
      run(
        () => setGameInfo({ action: 'open' }),
        'En ny runde er åpnet',
        'Kunne ikke starte ny runde',
        () => {
          // Refresh stats after opening to show updated data
          refreshParticipantsStats();
        }
      );
    }
  };
</script>

<div class="lock-badge-container">
  <button
    type="button"
    aria-expanded={isExpanded}
    class={{
      'lock-badge': true,
      expanded: isExpanded,
      locked: isLocked,
      locking: countdown != null,
      grace: graceCountDown != null,
    }}
    onclick={toggleExpanded}
  >
    {#if $gameInfo !== null}
      <Icon svg={isLocked ? lockClosedIcon : lockOpenIcon} />
      <span role="status" aria-live="polite">
        {#if countdown != null}
          Låses om {countdown}
        {:else if graceCountDown != null}
          Henter siste stemmer ({graceCountDown})
        {:else}
          Status: {isLocked ? 'Låst' : 'Åpen'}
        {/if}
      </span>
    {/if}
  </button>

  {#if isExpanded}
    <div class="expanded-content">
      {#if !showConfirm}
        <div class="controls">
          {#if canLock}
            {#if hasCountdown}
              <p class="countdown-info">
                Låses om {countdown}
              </p>
              <button
                class="btn primary"
                onclick={() => handleAction('unlock')}
                disabled={isSubmitting}
              >
                Avbryt låsing
              </button>
            {:else}
              <label for="lock-preset" class="label">Lås om</label>
              <select
                id="lock-preset"
                class="preset-select"
                bind:value={selectedPreset}
              >
                {#each PRESETS as preset (preset.seconds)}
                  <option value={String(preset.seconds)}>{preset.label}</option>
                {/each}
              </select>
              <button
                class="btn primary danger"
                onclick={() => handleAction('lock')}
                disabled={isSubmitting}
              >
                Lås
              </button>
            {/if}
          {:else}
            <button
              class="btn primary"
              onclick={() => handleAction(resetFavorites ? 'open' : 'unlock')}
              disabled={isSubmitting}
            >
              Lås opp
            </button>
            <label class="checkbox-row">
              <input type="checkbox" bind:checked={resetFavorites} />
              Nullstill favoritter
            </label>
          {/if}
        </div>
      {:else}
        <div class="confirm">
          <p class="confirm-title">{confirmTitle}</p>
          <p class="confirm-body">{confirmBody}</p>
          <div class="confirm-actions">
            <button class="btn clear" onclick={handleCancel}> Avbryt </button>
            <button
              class="btn primary"
              class:danger={pendingAction === 'lock'}
              onclick={handleConfirm}
              disabled={isSubmitting}
            >
              {pendingAction === 'lock'
                ? 'Lås'
                : pendingAction === 'unlock'
                  ? 'Lås opp'
                  : 'Start'}
            </button>
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .lock-badge-container {
    position: relative;
  }

  .lock-badge {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-1) var(--space-3);
    border: none;
    border-radius: var(--radius-pill);
    background: var(--success-subtle);
    color: var(--success);
    font-size: var(--text-sm);
    font-weight: var(--nrk-sans-weight-medium);
    white-space: nowrap;
    cursor: pointer;
    transition: all var(--duration-fast) var(--ease);

    &:hover {
      background: var(--success);
      color: var(--text-on-accent);
    }

    &.expanded {
      border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    }
  }

  .lock-badge.locked {
    background: var(--danger-subtle);
    color: var(--danger);

    &:hover {
      background: var(--danger);
      color: var(--text-on-accent);
    }
  }

  .lock-badge.locking {
    background: var(--warning-subtle);
    color: var(--warning);
    box-shadow: 0 0 0 var(--focus-ring-width) var(--warning);

    &:hover {
      background: var(--warning);
      color: var(--text-on-accent);
    }
  }

  .lock-badge.grace {
    background: var(--accent-subtle);
    color: var(--accent);

    &:hover {
      background: var(--accent);
      color: var(--text-on-accent);
    }
  }

  .lock-badge:focus-visible {
    outline: none;
    box-shadow: 0 0 0 var(--focus-ring-width) var(--focus-ring);
  }

  .lock-badge.locking:focus-visible {
    box-shadow: 0 0 0 var(--focus-ring-width) var(--warning);
  }

  .expanded-content {
    position: absolute;
    inset-block-start: 100%;
    inset-inline-end: 0;
    z-index: 10;
    min-inline-size: 20rem;
    padding: var(--space-4);
    border: var(--border-width) solid var(--border);
    border-block-start: none;
    border-radius: 0 0 var(--radius-lg) var(--radius-lg);
    background: var(--surface);
    box-shadow: var(--shadow-md);
    animation: slideDown var(--duration-fast) var(--ease);
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-0.5rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .controls {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .label {
    font-size: var(--text-sm);
    font-weight: var(--nrk-sans-weight-medium);
    color: var(--text-muted);
  }

  .countdown-info {
    padding: var(--space-3);
    border-radius: var(--radius-md);
    background: var(--warning-subtle);
    color: var(--warning);
    font-size: var(--text-sm);
    font-weight: var(--nrk-sans-weight-medium);
    text-align: center;
  }

  .preset-select {
    inline-size: 100%;
    block-size: var(--nrk-field-height);
    padding-inline: var(--space-3);
    border: var(--border-width) solid var(--nrk-field-color-border);
    border-radius: var(--nrk-field-radius);
    background: var(--nrk-field-color-background);
    color: var(--text);
  }

  .checkbox-row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-sm);
    color: var(--text);
    cursor: pointer;
    user-select: none;

    & input[type='checkbox'] {
      inline-size: 1.125rem;
      block-size: 1.125rem;
      cursor: pointer;
    }
  }

  .confirm {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .confirm-title {
    font-weight: var(--nrk-sans-weight-bold);
    color: var(--text);
  }

  .confirm-body {
    color: var(--text-muted);
    font-size: var(--text-sm);
  }

  .confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-2);
    margin-block-start: var(--space-2);
  }

  @media (width < 40rem) {
    .expanded-content {
      inset-inline-end: auto;
      inset-inline-start: 0;
      min-inline-size: auto;
      inline-size: calc(100vw - var(--space-8));
    }
  }
</style>
